import { lazy, Suspense, type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AuthSessionPayload } from '../types';
import { LoginPage } from './LoginPage';
import { useAuthConfig, useAuthSession, useSessionState } from '../hooks/useAuth';
import { fetchJson, RequestError } from '../lib/api';
import { removeBrowserPreference } from '../lib/browserPreferences';
import { AppShellFallback } from '../components/RouteFallback';
import { PrivacyChoicesButton } from '../components/consent/PrivacyChoicesButton';

// The login route is the entry point for every unauthenticated visitor, so
// nothing behind authentication may sit on its critical path. Both of these
// pull in the app shell and its page tree; loading them lazily keeps that code
// out of the login chunk entirely.
const AuthenticatedApp = lazy(() => import('../AuthenticatedApp'));
const OnboardingPage = lazy(() =>
  import('./OnboardingPage').then((module) => ({ default: module.OnboardingPage }))
);



function ConfiguredApp({
  authBaseUrl,
  passkey,
  providers,
}: {
  authBaseUrl: string;
  passkey?: boolean;
  providers?: { github?: boolean; google?: boolean; microsoft?: boolean };
}) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionScope = location.pathname.startsWith('/app') ? '/app' : location.pathname;
  const sessionState = useSessionState(authBaseUrl, sessionScope);
  const token = sessionState.data?.bearerToken ?? null;
  const isLoginPath = location.pathname.startsWith('/login');
  const sessionQuery = useAuthSession(token, !isLoginPath);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // If a session token exists and we're on the login page, redirect to the app.
  // This completes OTP and other auth flows that set the cookie but keep the user on /login.
  useEffect(() => {
    if (isLoginPath && token) {
      const params = new URLSearchParams(location.search);
      const redirectTo = params.get('redirectTo');
      if (redirectTo?.startsWith('/') && !redirectTo.startsWith('//')) {
        if (redirectTo.startsWith('/api/')) {
          window.location.href = redirectTo;
        } else {
          navigate(redirectTo, { replace: true });
        }
        return;
      }
      navigate('/app', { replace: true });
    }
  }, [isLoginPath, token, navigate, location.search]);

  const acceptInviteMutation = useMutation({
    mutationFn: (inviteToken: string) =>
      fetchJson<{ success: true; session: AuthSessionPayload }>('/api/settings/invitations/accept', token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: inviteToken }),
      }),
    onSuccess: async () => {
      removeBrowserPreference('selectedOrganizationId');
      removeBrowserPreference('selectedProjectId');
      const params = new URLSearchParams(location.search);
      params.delete('invite');
      navigate(
        params.toString()
          ? `${location.pathname}?${params.toString()}`
          : location.pathname,
        { replace: true }
      );
      await queryClient.invalidateQueries();
    },
    onError: (error: Error) => {
      setInviteError(error.message);
      const params = new URLSearchParams(location.search);
      params.delete('invite');
      navigate(
        params.toString()
          ? `${location.pathname}?${params.toString()}`
          : location.pathname,
        { replace: true }
      );
    },
  });

  // Persist invite token across auth redirects that may drop query params
  useEffect(() => {
    const urlInvite = new URLSearchParams(location.search).get('invite');
    if (urlInvite) {
      sessionStorage.setItem('perspectify:pending-invite', urlInvite);
    }
  }, [location.search]);

  useEffect(() => {
    if (!token || acceptInviteMutation.isPending) {
      return;
    }

    const inviteToken =
      new URLSearchParams(location.search).get('invite') ??
      sessionStorage.getItem('perspectify:pending-invite');
    if (!inviteToken) {
      return;
    }

    sessionStorage.removeItem('perspectify:pending-invite');
    void acceptInviteMutation.mutateAsync(inviteToken);
  }, [acceptInviteMutation, location.search, token]);

  const logout = async () => {
    await fetch(`${authBaseUrl}/sign-out`, { method: 'POST', credentials: 'include' }).catch(() => {});
    queryClient.removeQueries({ queryKey: ['auth-session-state'] });
    queryClient.removeQueries({ queryKey: ['auth-session'] });
    navigate('/login', { replace: true });
  };

  let content: ReactNode;

  if (sessionState.isLoading) {
    content = (
      <AuthStatusScreen tone="loading">Restoring session…</AuthStatusScreen>
    );
  } else if (sessionState.error) {
    content = (
      <AuthStatusScreen tone="error">
        <div className="flex flex-col items-center gap-4">
          <span>{sessionState.error.message}</span>
          <button
            type="button"
            onClick={() => void sessionState.refetch()}
            disabled={sessionState.isFetching}
            className="min-h-[44px] rounded-full border border-rose-400/30 px-5 py-2 text-sm font-medium transition hover:bg-rose-500/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-300 disabled:opacity-60"
          >
            {sessionState.isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      </AuthStatusScreen>
    );
  } else if (isLoginPath) {
    content = <LoginPage authBaseUrl={authBaseUrl} passkey={passkey} providers={providers} />;
  } else if (!token) {
    content = <LoginPage authBaseUrl={authBaseUrl} passkey={passkey} providers={providers} />;
  } else if (sessionQuery.error) {
    if (sessionQuery.error instanceof RequestError && sessionQuery.error.status === 401) {
      void fetch(`${authBaseUrl}/sign-out`, { method: 'POST', credentials: 'include' }).catch(() => {});
      queryClient.removeQueries({ queryKey: ['auth-session-state'] });
      queryClient.removeQueries({ queryKey: ['auth-session'] });
      content = <LoginPage authBaseUrl={authBaseUrl} passkey={passkey} providers={providers} />;
    } else {
      content = (
        <AuthStatusScreen tone="error">{sessionQuery.error.message}</AuthStatusScreen>
      );
    }
  } else if (sessionQuery.isLoading || !sessionQuery.data) {
    content = (
      <AuthStatusScreen tone="loading">Loading workspace access…</AuthStatusScreen>
    );
  } else if (sessionQuery.data.needsOnboarding) {
    content = (
      <Suspense fallback={<AppShellFallback label="Loading onboarding…" />}>
        <OnboardingPage token={token} user={sessionQuery.data.user} />
      </Suspense>
    );
  } else {
    content = (
      <Suspense fallback={<AppShellFallback />}>
        <AuthenticatedApp token={token} session={sessionQuery.data} logout={logout} />
      </Suspense>
    );
  }

  return (
    <>
      {inviteError && (
        <div className="fixed left-1/2 top-4 z-[2100] w-full max-w-xl -translate-x-1/2 px-4">
          <div className="rounded-2xl border border-rose-400/30 bg-rose-500/90 px-4 py-3 text-sm text-white shadow-xl">
            {inviteError}
          </div>
        </div>
      )}
      {content}
    </>
  );
}


/**
 * Full-screen session and configuration states. They render before the login page, the
 * onboarding page or the sidebar exist, so they carry their own "Privacy choices" entry: the
 * app hides the SDK's floating launcher everywhere.
 */
function AuthStatusScreen({ tone, children }: { tone: 'loading' | 'error'; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto flex min-h-screen max-w-4xl flex-col items-center justify-center gap-4 px-6">
        <div
          role={tone === 'error' ? 'alert' : 'status'}
          className={
            tone === 'error'
              ? 'rounded-3xl border border-rose-400/30 bg-rose-500/10 px-6 py-5 text-sm text-rose-100'
              : 'rounded-3xl border border-slate-800 bg-slate-900/80 px-6 py-5 text-sm text-slate-300'
          }
        >
          {children}
        </div>
        <PrivacyChoicesButton variant="link" />
      </div>
    </div>
  );
}

export function AuthEntrypoint() {
  const authConfigQuery = useAuthConfig(true);

  if (authConfigQuery.isLoading) {
    return (
      <AuthStatusScreen tone="loading">Loading authentication config…</AuthStatusScreen>
    );
  }

  if (authConfigQuery.error || !authConfigQuery.data?.authBaseUrl) {
    return (
      <AuthStatusScreen tone="error">{authConfigQuery.error?.message || 'Authentication is not configured for this deployment.'}</AuthStatusScreen>
    );
  }

  return (
    <ConfiguredApp
      authBaseUrl={authConfigQuery.data.authBaseUrl}
      passkey={authConfigQuery.data.passkey}
      providers={authConfigQuery.data.providers}
    />
  );
}
